import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { useQuery, useSubscription } from "@apollo/react-hooks";
import { MyLeaveFilterBar, MyLeaveNewRequestDialog } from "my-leave/components";
import {
  myLeaveRequests,
  myLeaveRequestsIsMobile,
} from "my-leave/configs/table-defs";
import {
  MY_LEAVE_GET_REQUESTS_BY_CONTRACT,
  MY_LEAVE_REQUESTS_SUBSCRIPTION,
} from "my-leave/graphql";
import myLeaveCoverage from "my-leave/utils/leave-coverage";
import { OcDataTable, OcErrorBoundary } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import {
  useOcClientState,
  useOcTableState,
  useOcCurrentUserState,
  useOcPolling,
} from "oc/hooks";
import { getFilterValues } from "oc/utils";
import getSortedList from "oc/utils/get-sorted-list";

// FIX #3: Konstans a komponensen KÍVÜL - nem jön létre újra minden rendereléskor
const TABLE_INITIAL_DATA = {
  ...initialTableData,
  sortField: "period",
  pageSize: 5,
  filterField: "state",
  filterSelected: ["requested"],
};

type MyLeaveMyLeaveRequestsTableProps = {
  selectedYear: number;
  disabledNewRequest: boolean;
  isMobile: boolean;
};

export const MyLeaveMyLeaveRequestsTable: React.FC<
  MyLeaveMyLeaveRequestsTableProps
> = ({ selectedYear, disabledNewRequest, isMobile }) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();

  let { contractCode } = useOcCurrentUserState();

  const [, setRequestedLeaveDays] = useOcClientState(
    "requestedLeaveDays",
    { base: 0, study: 0, covid: 0, eszjtv: 0 },
  );
  const [showNewItemDialog, setShowNewItemDialog] = useOcClientState(
    "showNewLeaveRequestDialog",
    false as boolean,
  );

  const handleShowNewItemDialog = (newState: boolean) => {
    setShowNewItemDialog(newState);
  };

  const [selectedRowId, setSelectedRowId] = React.useState<
    string | number | undefined
  >(undefined);

  const originalonSelectRow = (o: any) => {
    if (isMobile) {
      history.push(`/my-leave/my-request/${o?.row?._id}`);
    } else {
      history.push(`/my-leave/my-request/${o?._id}`);
    }
  };

  const handleSelectRequest = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id);
    } else {
      originalonSelectRow(o);
    }
  };

  const { data, loading, error, refetch } = useQuery(
    MY_LEAVE_GET_REQUESTS_BY_CONTRACT,
    {
      fetchPolicy: "cache-and-network",
      pollInterval: useOcPolling(1000 * 60),
      skip: !contractCode,
      variables: { contract: contractCode, year: selectedYear.toString() },
    },
  );

  if (error) {
    console.log({
      component: "MyLeaveRequests",
      errorDescription:
        "Error occured when fetch data with MY_LEAVE_GET_REQUESTS_BY_CONTRACT query",
      error: error,
    });
  }

  // Query for all leave requests without year filter for validation
  const { data: allData, error: allError, refetch: allRefetch } = useQuery(
    MY_LEAVE_GET_REQUESTS_BY_CONTRACT,
    {
      fetchPolicy: "cache-and-network",
      skip: !contractCode,
      variables: { contract: contractCode },
    },
  );

  if (allError) {
    console.log({
      component: "MyLeaveRequests",
      errorDescription:
        "Error occured when fetch all data with MY_LEAVE_GET_REQUESTS_BY_CONTRACT query for validation",
      error: allError,
    });
  }

  const { error: subscriptionError } = useSubscription(
    MY_LEAVE_REQUESTS_SUBSCRIPTION,
    {
      onData: () => {
        refetch();
        allRefetch();
      },
    },
  );

  if (subscriptionError) {
    console.log({
      component: "MyLeaveRequests",
      errorDescription:
        "Error occured when subscribe data with MY_LEAVE_REQUESTS_SUBSCRIPTION query",
      error: error,
    });
  }

  let resultData = data?.leaveRequestsByContract || [];
  let allResultData = allData?.leaveRequestsByContract || [];

  let result = _.cloneDeep(resultData);
  let allResult = _.cloneDeep(allResultData);

  type LeaveRequestItem = {
    _id: string;
    startDate: string;
    endDate: string;
    createdAt: string;
    type: string;
    state: string;
    leaveType?: string;
    workDays?: number;
    effectiveWorkDays?: number;
    period?: string;
    [key: string]: any;
  };

  // Compute requestedLeaveDays from result
  const computedRequestedLeaveDays = React.useMemo(() => {
    const days = { base: 0, study: 0, covid: 0, eszjtv: 0 };
    const now = new Date();

    result.forEach((item: LeaveRequestItem) => {
      const itemDate = new Date(item?.createdAt);
      const inCurrentYear = now.getFullYear() === itemDate.getFullYear();

      if (item.type === "leave" && item.state === "requested" && inCurrentYear) {
        if (item.leaveType === "Q") {
          days.covid = days.covid + (item.workDays ?? 0);
        } else if (item.leaveType === "D") {
          days.eszjtv = days.eszjtv + (item.workDays ?? 0);
        } else if (item.leaveType === "T") {
          days.study = days.study + (item.workDays ?? 0);
        } else {
          days.base = days.base + (item.workDays ?? 0);
        }
      }
    });
    return days;
  }, [result]);

  // Transform result items
  const transformedResult = React.useMemo(() => {
    return result.map((item: LeaveRequestItem) => {
      const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      };
      let startDate = new Date(item.startDate).toLocaleDateString(
        i18n.language,
        options,
      );
      let endDate = new Date(item.endDate).toLocaleDateString(
        i18n.language,
        options,
      );

      let createdAt = new Date(item.createdAt).toLocaleString(i18n.language);
      if (item._id.startsWith("jd-")) {
        if ((item.effectiveWorkDays ?? 0) > 0) {
          createdAt = t(`MY_LEAVE_RECORDED_IN_JDOLBER`);
        } else {
          createdAt = t(`MY_LEAVE_CANCELLED_IN_JDOLBER`);
        }
      }

      return {
        ...item,
        createdAt,
        period: `${startDate} - ${endDate}`,
        type: t(`M${item.type}`),
      };
    });
  }, [result, i18n.language, t]);

  // FIX #1: useEffect MEGFELELŐ dependency array-jel
  const requestedLeaveDaysJson = JSON.stringify(computedRequestedLeaveDays);
  React.useEffect(() => {
    setRequestedLeaveDays(JSON.parse(requestedLeaveDaysJson));
  }, [requestedLeaveDaysJson, setRequestedLeaveDays]);

  const tableId = "myLeaveRequestsTable";
  const [tableData, setTableData] = useOcTableState(tableId, TABLE_INITIAL_DATA);

  const sortedResult = React.useMemo(() => {
    const sorted = [...transformedResult];
    sorted.sort(
      getSortedList(
        tableData?.sortDirection === "desc"
          ? tableData?.sortField
          : `-${tableData?.sortField}`,
      ),
    );
    return sorted;
  }, [transformedResult, tableData?.sortDirection, tableData?.sortField]);

  // FIX #2: filterValues inicializálás useEffect-ben, NEM renderelés közben
  const filterValuesInitialized = React.useRef(false);
  React.useEffect(() => {
    if (
      !filterValuesInitialized.current &&
      tableData?.filterField &&
      tableData?.filterValues?.length === 0 &&
      sortedResult.length > 0
    ) {
      const filterValues = getFilterValues(tableData.filterField, sortedResult);
      if (filterValues.length > 0) {
        filterValuesInitialized.current = true;
        setTableData({ filterValues });
      }
    }
  }, [tableData?.filterField, tableData?.filterValues?.length, sortedResult, setTableData]);

  // Filter rows
  const filteredRows = React.useMemo(() => {
    if (tableData?.filterSelected?.length > 0) {
      return sortedResult.filter((item) => {
        let key = item[tableData?.filterField];
        return tableData?.filterSelected.includes(key);
      });
    }
    return sortedResult;
  }, [sortedResult, tableData?.filterSelected, tableData?.filterField]);

  let list = {
    total: filteredRows.length,
    page: tableData?.page,
    pageSize: tableData?.pageSize || initialTableData.pageSize,
    rows: filteredRows,
  };

  let loadingState = loading && !data;

  let tableDef;
  if (isMobile) {
    tableDef = myLeaveRequestsIsMobile;
  } else {
    tableDef = myLeaveRequests;
  }

  const filteredTableDef = {
    id: myLeaveRequests?.id,
    columns: myLeaveRequests?.columns?.filter(
      (column) =>
        !myLeaveRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const handleFilterBarChange = (data: { filterSelected: string[] }) => {
    setTableData({
      ...tableData,
      ...data, // csak a filterSelected-et frissíti
    });
  };

  return (
    <React.Fragment>
      <OcErrorBoundary>
        <MyLeaveFilterBar
          tableData={tableData}
          setTableData={handleFilterBarChange}
          loading={loadingState}
          onShowNewRequestDialog={handleShowNewItemDialog}
          disabledNewRequest={disabledNewRequest}
        />
      </OcErrorBoundary>
      <OcErrorBoundary>
        <OcDataTable
          data={list}
          loading={loadingState}
          tableDef={tableDef}
          tableData={tableData}
          setTableData={setTableData}
          pagination={true}
          onSelectRow={handleSelectRequest}
          localPaging={true}
          selectedRowId={selectedRowId}
          filteredTableDef={filteredTableDef}
          handleMoreButton={originalonSelectRow}
        />
      </OcErrorBoundary>
      <OcErrorBoundary>
        <MyLeaveNewRequestDialog
          onShowDialog={handleShowNewItemDialog}
          showDialog={showNewItemDialog}
          onCoverage={(params: any) => myLeaveCoverage(params, allResult)}
          assignEmployeeCode={null}
          assignContractCode={null}
          leaveRequests={allResult}
        />
      </OcErrorBoundary>
    </React.Fragment>
  );
};

export default MyLeaveMyLeaveRequestsTable;
